import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import * as authApi from './authApi';
import * as authStorage from './authStorage';
import type { User } from '../../types/user';

type AuthContextValue = {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState<string | null>(() => authStorage.getToken());
  const [user, setUserState] = useState<User | null>(() => authStorage.getUser());

  const login = useCallback(async (email: string, password: string) => {
    const response = await authApi.login({ email, password });
    authStorage.setToken(response.token);
    authStorage.setUser(response.user);
    setTokenState(response.token);
    setUserState(response.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // best-effort — clear local state regardless so the user can recover from a stale token
    }
    authStorage.clearSession();
    setTokenState(null);
    setUserState(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, token, isAuthenticated: Boolean(token), login, logout }),
    [user, token, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
